import axios, { AxiosInstance } from 'axios';
import { getApiConfig } from '../config/api';
import { resetAuthSessionExpiredNotification } from './authSession';

const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const SSO_LOGIN_HINT_KEY = 'ssoLoginHint';

class TokenService {
  private static instance: TokenService;
  private tokenRefreshPromise: Promise<string | null> | null = null;
  private axiosInstance: AxiosInstance;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: getApiConfig().BACKEND_BASE_URL,
      // Removed timeout for better user experience
    });
  }

  public static getInstance(): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService();
    }
    return TokenService.instance;
  }

  // Get token from localStorage
  public getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  // Set token in localStorage and axios headers
  public setToken(token: string, refreshToken?: string): void {
    localStorage.setItem(TOKEN_KEY, token);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    resetAuthSessionExpiredNotification();
    this.setAxiosAuthHeader(token);
  }

  // Remove token from localStorage and axios headers
  public removeToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SSO_LOGIN_HINT_KEY);
    delete this.axiosInstance.defaults.headers.common.Authorization;
  }

  // Microsoft account used for the SmartCHG SSO login — reused for silent SSO into embedded apps
  public getSsoLoginHint(): string | null {
    return localStorage.getItem(SSO_LOGIN_HINT_KEY);
  }

  public setSsoLoginHint(loginHint?: string | null): void {
    if (loginHint) localStorage.setItem(SSO_LOGIN_HINT_KEY, loginHint);
    else localStorage.removeItem(SSO_LOGIN_HINT_KEY);
  }

  // Set axios authorization header
  public setAxiosAuthHeader(token: string): void {
    this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  // Check if token exists
  public hasToken(): boolean {
    return !!this.getToken();
  }

  // Check if token is expired by decoding JWT payload
  public isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) {
      return true;
    }

    try {
      // Decode JWT token (base64 decode the payload)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT token format');
        return true;
      }

      // Decode payload (second part)
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;

      if (!exp) {
        console.warn('Token has no expiration claim');
        return true;
      }

      // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
      const isExpired = Date.now() >= exp * 1000;
      
      if (isExpired) {
        console.warn('Token is expired', { exp, now: Date.now() });
      }
      
      return isExpired;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      // If we can't decode, assume expired for safety
      return true;
    }
  }

  // Get user from localStorage
  public getUser(): any | null {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  // Set user in localStorage
  public setUser(user: any): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  // Validate token locally by decoding the JWT and checking `exp`. No HTTP.
  // The backend does the authoritative check on every protected request
  // (auth middleware), so there's no value in a pre-flight server round-trip.
  public async validateToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;
    this.setAxiosAuthHeader(token);
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
      );
      const exp = Number(payload.exp) || 0;
      const now = Math.floor(Date.now() / 1000);
      // Accept tokens that still have > 10s before expiry.
      return exp > now + 10;
    } catch (e) {
      console.warn('⚠️ Local JWT validation failed (malformed token):', e);
      return false;
    }
  }

  // Get refresh token from localStorage
  public getRefreshToken(): string | null {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) return refreshToken;

    const legacyRefreshToken = localStorage.getItem('refresh_token');
    if (legacyRefreshToken) {
      localStorage.setItem('refreshToken', legacyRefreshToken);
      return legacyRefreshToken;
    }

    return null;
  }

  // Refresh token with retry logic
  public async refreshToken(): Promise<string | null> {
    const currentToken = this.getToken();
    const refreshToken = this.getRefreshToken();
    
    if (!currentToken || !refreshToken) {
      console.warn('No token or refresh token available for refresh');
      return null;
    }

    // If already refreshing, return the existing promise
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.tokenRefreshPromise = this.performTokenRefresh(refreshToken);
    
    try {
      const newToken = await this.tokenRefreshPromise;
      return newToken;
    } finally {
      this.tokenRefreshPromise = null;
    }
  }

  private async performTokenRefresh(refreshToken: string): Promise<string | null> {
    const maxAttempts = 2;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const result = await this.tryPerformTokenRefresh(refreshToken, attempt);
      if (result.token) {
        return result.token;
      }

      if (!result.retryable || attempt >= maxAttempts) {
        return null;
      }

      console.warn('Retrying token refresh once after transient failure');
    }

    return null;
  }

  private async tryPerformTokenRefresh(
    refreshToken: string,
    attempt: number
  ): Promise<{ token: string | null; retryable: boolean }> {
    try {
      console.log(`Attempting token refresh... (attempt ${attempt})`);
      // SmartCHG /auth/refresh contract: request body is { refresh_token },
      // response body is { access_token, refresh_token, token_type, expires_in }.
      const response = await this.axiosInstance.post('/auth/refresh', { refresh_token: refreshToken });
      const newToken = response.data?.access_token;
      const newRefreshToken = response.data?.refresh_token;

      if (newToken) {
        console.log('Token refresh successful');
        this.setToken(newToken, newRefreshToken);
        return { token: newToken, retryable: false };
      }

      console.warn('No new access_token received from /auth/refresh');
      return { token: null, retryable: false };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Token refresh failed:', message);
      if (axios.isAxiosError(error) && error.response) {
        console.error('Token refresh error response:', error.response.status, error.response.data);
      }
      // Callers decide whether to log out; always clear the in-flight promise in refreshToken().
      return { token: null, retryable: this.isRetryableRefreshError(error) };
    }
  }

  private isRetryableRefreshError(error: unknown): boolean {
    if (!axios.isAxiosError(error)) {
      return true;
    }

    const status = error.response?.status;

    if (!status) {
      return true;
    }

    if (status === 400 || status === 401 || status === 403) {
      return false;
    }

    return status >= 500 || status === 408 || status === 429;
  }

  // Initialize token from localStorage
  public initializeToken(): void {
    const token = this.getToken();
    if (token) {
      this.setAxiosAuthHeader(token);
    }
  }
}

export const tokenService = TokenService.getInstance();
